import { useViewListContext } from "@/context/ViewListContext";
import { useTranslation } from "@vixi/localization";
import { Button, Img, useGoBack } from "@vixi/ui";
import { assert } from "@vixi/utils";
import { ComponentPropsWithoutRef, forwardRef, useCallback, useMemo, useState } from "react";
import { Path, PathValue, UseFormReturn } from "react-hook-form";
import {
  preference_tree,
  PreferenceFormValues,
  PreferenceOption,
  PreferenceTree,
} from "./PreferenceForm";

interface CharacterPreferenceOptionProps<k extends string = string, subK extends string = string> {
  option: PreferenceOption<k, subK>;
  onClick: (option: PreferenceOption<k, subK>) => void;
}
type CharacterPreferenceOptionRootProps = Omit<ComponentPropsWithoutRef<"button">, "color">;

export const CharacterPreferenceOptionRoot = forwardRef<
  HTMLButtonElement,
  CharacterPreferenceOptionRootProps
>((props, ref) => (
  <Button ref={ref} variant="card" className="text-foreground flex flex-col p-4.5" {...props} />
));

export const CharacterPreferenceOption = <k extends string = string, subK extends string = string>({
  onClick,
  option,
}: CharacterPreferenceOptionProps<k, subK>) => {
  const { t } = useTranslation();

  return (
    <CharacterPreferenceOptionRoot onClick={() => onClick(option)}>
      <h4 className="text-center">{t(option.label)}</h4>
      <div className="mt-4.25 flex">
        {option.images.map((image) => (
          <Img key={image} contain src={image} alt={t(option.label)} className="h-65" />
        ))}
      </div>
    </CharacterPreferenceOptionRoot>
  );
};

interface SelectCharacterPreferencesProps<T extends Record<string, any> = PreferenceFormValues> {
  preferencesForm: UseFormReturn<T>;
  preferenceTree?: PreferenceTree<keyof T, keyof T>;
  stepBack?: () => void;
  onCompleted?: (preferences: T) => void;
}

/**
 * TODO: Fix the types (remove all "as any")
 */
export const SelectCharacterPreferences = <T extends Record<string, any> = PreferenceFormValues>({
  preferencesForm,
  preferenceTree = preference_tree,
  onCompleted,
}: SelectCharacterPreferencesProps<T>) => {
  const { t } = useTranslation();
  const flow = useViewListContext();

  const { watch } = preferencesForm;

  const [steps, setSteps] = useState<PreferenceTree<keyof T, keyof T>[]>([preferenceTree]);
  const currentStep = steps[steps.length - 1];

  // Navigation logic
  const prevStep = useCallback(() => {
    setSteps((prev) => {
      const newSteps = [...prev];
      newSteps.pop();
      return newSteps;
    });
  }, [setSteps]);
  const onBack = useMemo(
    () => (steps.length > 1 ? prevStep : (flow?.prevStep ?? null)),
    [steps.length, flow],
  );
  useGoBack(onBack, {
    allowChanges: true,
  });

  const handleNextStep = (option: PreferenceOption<Exclude<keyof T, number | symbol>, string>) => {
    assert(typeof currentStep.key === "string", "Current step should not be undefined");

    preferencesForm.setValue(currentStep.key as Path<T>, option.value as PathValue<T, Path<T>>);

    const nextStep = (() => {
      // Check if the current step has subPreferences[] and return the first one
      if (option.subPreferences?.length) {
        return option.subPreferences[0];
      }

      // Try finding the parent subPreferences[]
      for (let i = 1; i < steps.length; i++) {
        const step = steps[steps.length - i - 1];

        // Check if the current step is a sub_preference of the previous step
        const selectedOption = step.options.findIndex(
          (opt) => opt.value === watch(step.key as any),
        );
        if (selectedOption !== -1 && step.options[selectedOption].subPreferences) {
          const subPreferences = step.options[selectedOption].subPreferences;

          // Find the current step in the subPreferences[] of the previous step
          const subPreferenceIndex = subPreferences?.findIndex((sp) => sp.key === currentStep.key);
          if (subPreferences.length > subPreferenceIndex + 1 && subPreferenceIndex !== -1) {
            return subPreferences[subPreferenceIndex + 1];
          }
        }
      }
    })();

    if (nextStep) setSteps((prev) => [...prev, nextStep]);
    else if (onCompleted) onCompleted(preferencesForm.getValues());
  };

  return (
    <div className="m-auto">
      <h3 className="mb-13.75 text-center">{t(currentStep.title)}:</h3>
      <div className="flex items-center gap-9.5">
        {currentStep.options.map((option) => {
          return (
            <CharacterPreferenceOption
              key={option.label}
              option={option as any}
              onClick={handleNextStep}
            />
          );
        })}
      </div>
    </div>
  );
};
